import React, { useState } from 'react';
import {
  Dialog,
  IconButton,
  IconChevronRight,
  SimpleTable,
  SimpleTableBody,
  SimpleTableContainer,
  SimpleTableData,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableWrapper,
  transitionAnimation
} from 'cdk-radial';
import styled, { css } from 'styled-components';
import { PLAN_TYPE, formatNumbers } from '../../utils/api-pricing';
import './BundlePriceDialog.css';

const planNameMap = {
  basic: 'Basic Plan',
  plus: 'Plus Plan',
  pro: 'Pro Plan',
};

const formatPlanName = (planName) => {
  return planNameMap[planName] || planName;
}

const BundlePriceDialog = ({ isOpen, onClose, plan, isAsync = false }) => {
  if (!plan) return null;

  const originalPlanType = plan.type?.toLowerCase() || plan.planType?.toLowerCase();
  const effectivePlanType =
    originalPlanType === PLAN_TYPE.SUBSCRIPTION
      ? PLAN_TYPE.BUNDLE_SUBSCRIPTION
      : originalPlanType;

  const isAdvanced = effectivePlanType === PLAN_TYPE.ADVANCED;
  const isBundleSubscription = effectivePlanType === PLAN_TYPE.BUNDLE_SUBSCRIPTION;
  const isLocationBased = !!plan.isDealerPlan;
  const displayPrice = plan.subscriptionPrice ?? plan.price;

  return (
    <Dialog
      id="bundle-price-dialog"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton={false}
      disableOverlayClicked={false}
      dataTestId="bundle-price-dialog"
      buttonsProps={[]}
      closeLabel="close"
      style={{ width: isAdvanced ? '860px' : '660px' }}
    >
      <div className="api-dir-pkg_bundle-dialog-body">
        <div className="api-dir-pkg_bundle-dialog-header">
          <div className="api-dir-pkg_bundle-dialog-header-title" id="bundle-price-dialog-title">
            <div className="api-dir-pkg_bundle-dialog-header-title-name">{formatPlanName(plan.planName)}</div>
          </div>
          {displayPrice != null && (
            <div className="api-dir-pkg_bundle-dialog-price">
              <div className="api-dir-pkg_bundle-dialog-price-main">
                <span className="api-dir-pkg_bundle-dialog-price-dollar">$</span>
                <span className="api-dir-pkg_bundle-dialog-price-amount">{formatNumbers(displayPrice)}</span>
                <span className="api-dir-pkg_bundle-dialog-price-unit">
                  {isLocationBased ? '/Location' : '/Month'}
                </span>
              </div>
              {isLocationBased && (
                <div className="api-dir-pkg_bundle-dialog-price-per-month">per month</div>
              )}
            </div>
          )}
        </div>
        <div className="api-dir-pkg_bundle-dialog-header-row">
          <p className="api-dir-pkg_bundle-dialog-description">{plan.description}</p>
        </div>

        <div className="api-dir-pkg_bundle-dialog-included-box">
          <div className="api-dir-pkg_bundle-dialog-included-title">What's Included?</div>

          {isAdvanced ? (
            <AdvancedContainersTable
              containers={plan.containers}
              isAsync={isAsync}
            />
          ) : isBundleSubscription ? (
            <SubscriptionContent isAsync={isAsync} />
          ) : (
            <div />
          )}
        </div>
      </div>
    </Dialog>
  );
};

const StyledChevronButton = styled(IconButton)`
  ${transitionAnimation('transform')}
  flex-shrink: 0;
  ${({ isopen }) =>
    isopen === 'true' &&
    css`
      transform: rotate(90deg);
    `}
`;

const ContainerRow = ({ container, index, isAsync }) => {
  const [isOpen, setIsOpen] = useState(false);
  const paths = isAsync ? (container.channels || []) : (container.endPoints || container.endpoints || []);
  const quotaText = container.isFree ? 'Unlimited' : `${formatNumbers(container.quota)} calls`;
  const overageText = `$${container.overage}`;

  return (
    <React.Fragment>
      <SimpleTableRow
        className="api-dir-pkg_bundle-dialog-container-row"
        data-testid={`bundle-dialog-container-row-${index}`}
      >
        <SimpleTableData className="api-dir-pkg_bundle-dialog-td--chevron">
          {paths.length > 0 && (
            <StyledChevronButton
              icon={<IconChevronRight />}
              onClick={() => setIsOpen(prev => !prev)}
              text={isOpen ? 'collapse' : 'expand'}
              isopen={String(isOpen)}
              data-testid={`bundle-dialog-chevron-${index}`}
            />
          )}
        </SimpleTableData>
        <SimpleTableData className="api-dir-pkg_bundle-dialog-td--name">
          {container.name}
        </SimpleTableData>
        <SimpleTableData className="api-dir-pkg_bundle-dialog-td--quota" textAlign="right">
          {quotaText}
        </SimpleTableData>
        <SimpleTableData className="api-dir-pkg_bundle-dialog-td--overage" textAlign="right">
          {overageText}
        </SimpleTableData>
      </SimpleTableRow>
      {isOpen &&
        paths.map((ep, epIdx) => {
          const summary = isAsync ? ep.channel : ep.summary || ep.route || 'Unknown';
          return (
            <SimpleTableRow
              key={epIdx}
              className="api-dir-pkg_bundle-dialog-endpoint-row"
            >
              <SimpleTableData />
              <SimpleTableData className="api-dir-pkg_bundle-dialog-td--endpoint-name">
                {summary}
              </SimpleTableData>
              <SimpleTableData />
              <SimpleTableData />
            </SimpleTableRow>
          );
        })}
    </React.Fragment>
  );
};

const AdvancedContainersTable = ({ containers, isAsync }) => (
  <SimpleTableWrapper style={{ border: 'none' }}>
    <SimpleTableContainer>
      <SimpleTable aria-label="Plan containers" className="api-dir-pkg_bundle-dialog-table">
        <SimpleTableHead>
          <SimpleTableRow>
            <SimpleTableHeader className="api-dir-pkg_bundle-dialog-th--chevron" />
            <SimpleTableHeader className="api-dir-pkg_bundle-dialog-th--name" />
            <SimpleTableHeader
              className="api-dir-pkg_bundle-dialog-th"
              textAlign="right"
            >
              Monthly Quota
            </SimpleTableHeader>
            <SimpleTableHeader
              className="api-dir-pkg_bundle-dialog-th"
              textAlign="right"
            >
              Overage Fee
            </SimpleTableHeader>
          </SimpleTableRow>
        </SimpleTableHead>
        <SimpleTableBody>
          {(containers || []).map((container, index) => (
            <ContainerRow
              key={index}
              container={container}
              index={index}
              isAsync={isAsync}
            />
          ))}
        </SimpleTableBody>
      </SimpleTable>
    </SimpleTableContainer>
  </SimpleTableWrapper>
);

const SubscriptionContent = ({ isAsync }) => (
  <div className="api-dir-pkg_bundle-dialog-subscription-content">
    <div className="api-dir-pkg_bundle-dialog-section-title">All Endpoints</div>
    <ul className="api-dir-pkg_bundle-dialog-bullet-list">
      <li>Unlimited calls per month with no overage fees.</li>
    </ul>
    {isAsync && (
      <>
        <div className="api-dir-pkg_bundle-dialog-section-title">All Channels</div>
        <ul className="api-dir-pkg_bundle-dialog-bullet-list">
          <li>Unlimited calls per month with no overage fees.</li>
        </ul>
      </>
    )}
  </div>
);

export { BundlePriceDialog };
